/**
 * Human-friendly recurring schedule to 6-field cron
 * (second, minute, hour, day, month, day-of-week).
 * Example every 5 minutes: 0 every-5 * * * * (step 5 on the minute field).
 */

export type RecurringKind = "minutes" | "hourly" | "daily" | "weekly" | "monthly";

export interface RecurringSchedule {
  kind: RecurringKind;
  /** Every N minutes, or every N hours. */
  interval: number;
  hour: number;
  minute: number;
  /** 0 = Sunday … 6 = Saturday. */
  weekdays: number[];
  monthDay: number;
}

const DOW = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"] as const;
const DOW_LABEL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export const DEFAULT_RECURRING_SCHEDULE: RecurringSchedule = {
  kind: "daily",
  interval: 5,
  hour: 10,
  minute: 0,
  weekdays: [1],
  monthDay: 1,
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const pad2 = (n: number) => String(n).padStart(2, "0");

export const formatClock = (hour: number, minute: number) => `${pad2(hour)}:${pad2(minute)}`;

export function recurringScheduleToCron(schedule: RecurringSchedule): string {
  const minute = clamp(Math.trunc(schedule.minute), 0, 59);
  const hour = clamp(Math.trunc(schedule.hour), 0, 23);
  switch (schedule.kind) {
    case "minutes": {
      const n = clamp(Math.trunc(schedule.interval) || 5, 1, 59);
      return n === 1 ? "0 * * * * *" : `0 */${n} * * * *`;
    }
    case "hourly": {
      const n = clamp(Math.trunc(schedule.interval) || 1, 1, 23);
      return n === 1 ? `0 ${minute} * * * *` : `0 ${minute} */${n} * * *`;
    }
    case "daily":
      return `0 ${minute} ${hour} * * *`;
    case "weekly": {
      const days = (schedule.weekdays.length > 0 ? schedule.weekdays : [1]).map(
        (d) => DOW[((d % 7) + 7) % 7],
      );
      return `0 ${minute} ${hour} * * ${[...new Set(days)].join(",")}`;
    }
    case "monthly":
      return `0 ${minute} ${hour} ${clamp(Math.trunc(schedule.monthDay) || 1, 1, 31)} * *`;
  }
}

function parseDowToken(token: string): number[] {
  const t = token.trim().toUpperCase();
  if (!t || t === "*" || t === "?") return [];
  const named = DOW.indexOf(t as (typeof DOW)[number]);
  if (named >= 0) return [named];
  if (/^\d+$/.test(t)) {
    const n = Number(t);
    if (n === 7) return [0];
    if (n >= 0 && n <= 6) return [n];
    return [];
  }
  const range = /^([A-Z]{3}|\d+)-([A-Z]{3}|\d+)$/.exec(t);
  if (!range) return [];
  const start = parseDowToken(range[1])[0];
  const end = parseDowToken(range[2])[0];
  if (start == null || end == null) return [];
  const out: number[] = [];
  if (start <= end) {
    for (let i = start; i <= end; i += 1) out.push(i);
  } else {
    for (let i = start; i <= 6; i += 1) out.push(i);
    for (let i = 0; i <= end; i += 1) out.push(i);
  }
  return out;
}

function parseDow(field: string): number[] {
  const days = field.split(",").flatMap(parseDowToken);
  return [...new Set(days)].sort((a, b) => a - b);
}

function isAnyDay(field: string): boolean {
  return field === "*" || field === "?";
}

export function cronToRecurringSchedule(cron: string | null | undefined): RecurringSchedule | null {
  const raw = cron?.trim().split(/\s+/) ?? [];
  const parts = raw.length === 5 ? ["0", ...raw] : raw;
  if (parts.length !== 6) return null;
  const [, minute, hour, dayOfMonth, month, dayOfWeek] = parts;
  if (month !== "*") return null;

  if (
    (minute === "*" || minute.startsWith("*/")) &&
    hour === "*" &&
    isAnyDay(dayOfMonth) &&
    isAnyDay(dayOfWeek)
  ) {
    const interval = minute === "*" ? 1 : Number(minute.slice(2));
    if (!Number.isInteger(interval) || interval < 1) return null;
    return { ...DEFAULT_RECURRING_SCHEDULE, kind: "minutes", interval };
  }

  if (
    /^\d+$/.test(minute) &&
    (hour === "*" || hour.startsWith("*/")) &&
    isAnyDay(dayOfMonth) &&
    isAnyDay(dayOfWeek)
  ) {
    const interval = hour === "*" ? 1 : Number(hour.slice(2));
    if (!Number.isInteger(interval) || interval < 1) return null;
    return {
      ...DEFAULT_RECURRING_SCHEDULE,
      kind: "hourly",
      interval,
      minute: Number(minute),
    };
  }

  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && /^\d+$/.test(dayOfMonth) && isAnyDay(dayOfWeek)) {
    return {
      ...DEFAULT_RECURRING_SCHEDULE,
      kind: "monthly",
      hour: Number(hour),
      minute: Number(minute),
      monthDay: Number(dayOfMonth),
    };
  }

  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && isAnyDay(dayOfMonth) && isAnyDay(dayOfWeek)) {
    return {
      ...DEFAULT_RECURRING_SCHEDULE,
      kind: "daily",
      hour: Number(hour),
      minute: Number(minute),
    };
  }

  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && isAnyDay(dayOfMonth) && !isAnyDay(dayOfWeek)) {
    const weekdays = parseDow(dayOfWeek);
    if (weekdays.length === 0) return null;
    return {
      ...DEFAULT_RECURRING_SCHEDULE,
      kind: "weekly",
      hour: Number(hour),
      minute: Number(minute),
      weekdays,
    };
  }

  return null;
}

/** Cron for a legacy HOURLY / DAILY / WEEKLY / MONTHLY row that has none stored. */
export function cronFromCadencePreset(cadence: string | null | undefined): string {
  switch ((cadence ?? "").toUpperCase()) {
    case "HOURLY":
      return recurringScheduleToCron({ ...DEFAULT_RECURRING_SCHEDULE, kind: "hourly", interval: 1, minute: 0 });
    case "WEEKLY":
      return recurringScheduleToCron({ ...DEFAULT_RECURRING_SCHEDULE, kind: "weekly", weekdays: [1] });
    case "MONTHLY":
      return recurringScheduleToCron({ ...DEFAULT_RECURRING_SCHEDULE, kind: "monthly" });
    default:
      return recurringScheduleToCron(DEFAULT_RECURRING_SCHEDULE);
  }
}

function weekdayList(days: number[]): string {
  const labels = days.map((d) => DOW_LABEL[((d % 7) + 7) % 7]);
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

export function describeRecurringSchedule(schedule: RecurringSchedule): string {
  const time = formatClock(schedule.hour, schedule.minute);
  switch (schedule.kind) {
    case "minutes":
      return schedule.interval === 1 ? "Every minute" : `Every ${schedule.interval} minutes`;
    case "hourly":
      return schedule.interval === 1
        ? `Every hour at :${pad2(schedule.minute)}`
        : `Every ${schedule.interval} hours at :${pad2(schedule.minute)}`;
    case "daily":
      return `Every day at ${time}`;
    case "weekly":
      return `Every ${weekdayList(schedule.weekdays.length ? schedule.weekdays : [1])} at ${time}`;
    case "monthly":
      return `On the ${ordinal(schedule.monthDay)} of each month at ${time}`;
  }
}

export function describeCron(cron: string | null | undefined): string | null {
  const schedule = cronToRecurringSchedule(cron);
  return schedule ? describeRecurringSchedule(schedule) : cron?.trim() || null;
}

export const WEEKDAY_OPTIONS = DOW.map((value, index) => ({ value: index, label: value, longLabel: DOW_LABEL[index] }));
export const MINUTE_INTERVAL_OPTIONS = [1, 5, 10, 15, 20, 30];
export const HOUR_INTERVAL_OPTIONS = [1, 2, 3, 4, 6, 12];
export const MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1);
